import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Flame, Dumbbell, Wheat, Droplet, Leaf, RotateCcw, Check } from "lucide-react";
import { SHAKE, MEAL, IMG } from "@/data/menu";
import { Reveal } from "@/components/site/Reveal";
import { useCart } from "@/context/CartContext";

const sum = (arr, k) => arr.reduce((s, x) => s + (x[k] || 0), 0);

function Chip({ label, sub, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-left rounded-2xl border px-4 py-3 transition-colors ${
        active ? "border-teal-brand bg-[#8A2231]/5" : "border-line hover:border-teal-brand/50"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={`grid place-items-center w-4 h-4 rounded-full border ${active ? "bg-[#8A2231] border-teal-brand" : "border-coffee-muted"}`}>
          {active && <Check size={11} className="text-white" />}
        </span>
        <span className="font-semibold text-sm text-coffee">{label}</span>
      </div>
      <span className="mt-1 block font-mono text-xs text-coffee-muted pl-6">{sub}</span>
    </button>
  );
}

function Bar({ icon: Icon, label, value, max, unit }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div>
      <div className="flex items-center gap-2 text-sm">
        <Icon size={15} className="text-teal-brand" />
        <span className="text-coffee-soft">{label}</span>
        <span className="ml-auto font-mono font-bold text-coffee">{Math.round(value)}{unit}</span>
      </div>
      <div className="mt-1.5 h-2 rounded-full bg-line overflow-hidden">
        <motion.div className="h-full bg-[#8A2231] rounded-full" animate={{ width: `${pct}%` }} transition={{ type: "spring", stiffness: 120, damping: 20 }} />
      </div>
    </div>
  );
}

export default function Builder() {
  const [mode, setMode] = useState("meal");
  const { add } = useCart();

  const [shakeBase, setShakeBase] = useState(SHAKE.bases[1]);
  const [shakeProtein, setShakeProtein] = useState(SHAKE.proteins[0]);
  const [shakeFruits, setShakeFruits] = useState([SHAKE.fruits[0]]);
  const [shakeAddons, setShakeAddons] = useState([]);

  const [mealBase, setMealBase] = useState(MEAL.bases[3]);
  const [mealProtein, setMealProtein] = useState(MEAL.proteins[0]);
  const [mealVeggies, setMealVeggies] = useState([MEAL.veggies[0], MEAL.veggies[1]]);
  const [mealSauce, setMealSauce] = useState(MEAL.sauces[1]);
  const [mealAddons, setMealAddons] = useState([]);

  const totals = useMemo(() => {
    if (mode === "shake") {
      const parts = [shakeBase, shakeProtein, ...shakeFruits, ...shakeAddons];
      return { calories: sum(parts, "calories"), protein: sum(parts, "protein"), carbs: sum(parts, "carbs"), fat: sum(parts, "fat"), fiber: 0 };
    }
    const parts = [mealBase, mealProtein, mealSauce, ...mealVeggies, ...mealAddons];
    return { calories: sum(parts, "calories"), protein: sum(parts, "protein"), carbs: sum(parts, "carbs"), fat: sum(parts, "fat"), fiber: sum(parts, "fiber") };
  }, [mode, shakeBase, shakeProtein, shakeFruits, shakeAddons, mealBase, mealProtein, mealVeggies, mealSauce, mealAddons]);

  const toggle = (list, set, item, single) => {
    if (single) return set(item);
    set(list.some((x) => x.name === item.name) ? list.filter((x) => x.name !== item.name) : [...list, item]);
  };
  const has = (list, item) => list.some((x) => x.name === item.name);

  const reset = () => {
    if (mode === "shake") { setShakeFruits([SHAKE.fruits[0]]); setShakeAddons([]); }
    else { setMealVeggies([MEAL.veggies[0], MEAL.veggies[1]]); setMealAddons([]); }
    toast("Builder reset");
  };

  return (
    <section id="builder" data-testid="builder-section" className="bg-beige noise relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 relative">
        <Reveal className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-teal-brand">Build Your Own · Live Macros</p>
          <h2 className="mt-3 font-display text-4xl sm:text-6xl font-bold tracking-tight text-coffee">Stack Your Fuel.</h2>
          <p className="mt-4 text-lg text-coffee-soft">Build a custom shake or a loaded recovery bowl your way. Watch the macros update live as you stack — no cap.</p>
        </Reveal>

        <div className="mt-8 inline-flex rounded-full border border-line bg-white p-1" data-testid="builder-mode-toggle">
          {[["meal", "Recovery Bowl"], ["shake", "Custom Shake"]].map(([k, l]) => (
            <button
              key={k}
              data-testid={`builder-mode-${k}`}
              onClick={() => setMode(k)}
              className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-colors ${mode === k ? "bg-[#8A2231] text-white" : "text-coffee-soft"}`}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="mt-10 grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2 space-y-8">
            {mode === "shake" ? (
              <>
                <Group title="Base" hint="pick one">
                  {SHAKE.bases.map((b) => <Chip key={b.name} testid={`shake-base-${b.name}`} label={b.name} sub={`${b.calories} kcal · ${b.protein}g P`} active={shakeBase.name === b.name} onClick={() => setShakeBase(b)} />)}
                </Group>
                <Group title="Fruits" hint="choose any">
                  {SHAKE.fruits.map((f) => <Chip key={f.name} testid={`shake-fruit-${f.name}`} label={f.name} sub={`${f.calories} kcal · ${f.carbs}g C`} active={has(shakeFruits, f)} onClick={() => toggle(shakeFruits, setShakeFruits, f)} />)}
                </Group>
                <Group title="Protein Powder" hint="pick one">
                  {SHAKE.proteins.map((p) => <Chip key={p.name} testid={`shake-protein-${p.name}`} label={p.name} sub={`${p.calories} kcal · ${p.protein}g P`} active={shakeProtein.name === p.name} onClick={() => setShakeProtein(p)} />)}
                </Group>
                <Group title="Supplement Add-ons" hint="optional">
                  {SHAKE.addons.map((a) => <Chip key={a.name} testid={`shake-addon-${a.name}`} label={a.name} sub={`${a.calories} kcal · ${a.protein}g P`} active={has(shakeAddons, a)} onClick={() => toggle(shakeAddons, setShakeAddons, a)} />)}
                </Group>
              </>
            ) : (
              <>
                <Group title="1 · Base" hint="pick one">
                  {MEAL.bases.map((b) => <Chip key={b.name} testid={`meal-base-${b.name}`} label={b.name} sub={`${b.calories} kcal · ${b.protein}g P`} active={mealBase.name === b.name} onClick={() => setMealBase(b)} />)}
                </Group>
                <Group title="2 · Protein" hint="pick one">
                  {MEAL.proteins.map((p) => <Chip key={p.name} testid={`meal-protein-${p.name}`} label={p.name} sub={`${p.calories} kcal · ${p.protein}g P`} active={mealProtein.name === p.name} onClick={() => setMealProtein(p)} />)}
                </Group>
                <Group title="3 · Veggies" hint="load up — free">
                  {MEAL.veggies.map((v) => <Chip key={v.name} testid={`meal-veggie-${v.name}`} label={v.name} sub={`${v.calories} kcal · ${v.fiber}g fiber`} active={has(mealVeggies, v)} onClick={() => toggle(mealVeggies, setMealVeggies, v)} />)}
                </Group>
                <Group title="4 · Sauce" hint="pick one">
                  {MEAL.sauces.map((s) => <Chip key={s.name} testid={`meal-sauce-${s.name}`} label={s.name} sub={`${s.calories} kcal · ${s.fat}g F`} active={mealSauce.name === s.name} onClick={() => setMealSauce(s)} />)}
                </Group>
                <Group title="5 · Add-ons" hint="optional">
                  {MEAL.addons.map((a) => <Chip key={a.name} testid={`meal-addon-${a.name}`} label={a.name} sub={`${a.calories} kcal · ${a.protein}g P`} active={has(mealAddons, a)} onClick={() => toggle(mealAddons, setMealAddons, a)} />)}
                </Group>
              </>
            )}
          </div>

          <div className="lg:sticky lg:top-24">
            <div className="rounded-3xl border border-line bg-white overflow-hidden" data-testid="builder-summary">
              <div className="relative h-36 overflow-hidden">
                <img src={mode === "shake" ? IMG.shakes : IMG.saladBowl} alt="Your build" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-coffee/40" />
                <div className="absolute bottom-3 left-4 text-white font-display text-xl font-bold">Your {mode === "shake" ? "Shake" : "Bowl"}</div>
              </div>
              <div className="p-6 space-y-5">
                <div className="flex items-end justify-between">
                  <span className="text-xs uppercase tracking-[0.2em] text-coffee-muted">Total Calories</span>
                  <span className="font-mono text-4xl font-bold text-coffee" data-testid="builder-total-calories">{Math.round(totals.calories)}</span>
                </div>
                <div className="space-y-3.5">
                  <Bar icon={Dumbbell} label="Protein" value={totals.protein} max={80} unit="g" />
                  <Bar icon={Wheat} label="Carbs" value={totals.carbs} max={120} unit="g" />
                  <Bar icon={Droplet} label="Fat" value={totals.fat} max={60} unit="g" />
                  <Bar icon={Leaf} label="Fiber" value={totals.fiber} max={30} unit="g" />
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    data-testid="builder-add-cart"
                    onClick={() => {
                      const price = mode === "shake" ? 260 : 360;
                      const nm = mode === "shake" ? "Custom Shake" : "Custom Recovery Bowl";
                      add({ key: `custom-${mode}-${Date.now()}`, name: nm, price, calories: Math.round(totals.calories), protein: Math.round(totals.protein) });
                      toast.success(`${nm} added`, { description: `${Math.round(totals.calories)} kcal · ${Math.round(totals.protein)}g protein · ₹${price}` });
                    }}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#8A2231] hover:bg-[#6E1926] text-white font-semibold py-3 transition-colors"
                  >
                    <Flame size={16} /> Add to Cart
                  </button>
                  <button data-testid="builder-reset" onClick={reset} className="grid place-items-center w-12 rounded-full border border-line text-coffee-soft hover:border-teal-brand transition-colors">
                    <RotateCcw size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Group({ title, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline gap-3 mb-3">
        <h3 className="font-display text-2xl font-bold text-coffee">{title}</h3>
        <span className="text-xs uppercase tracking-[0.15em] text-teal-brand">{hint}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">{children}</div>
    </div>
  );
}
